import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import api from "../utils/api";
import LogoutButton from "../components/LogoutButton";
import Sidebar from "../components/Sidebar";
import "../style/adminreport.css";

function AdminReports() {
  const navigate = useNavigate();
  const [reportsData, setReportsData] = useState([]);
  const [search, setSearch] = useState("");
  const [filterWing, setFilterWing] = useState("");
  const [filterFlat, setFilterFlat] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [modalImage, setModalImage] = useState(null);
  const rowsPerPage = 10;

  useEffect(() => {
    setLoading(true);
    api.get("/meter-readings/all-page")
      .then(response => {
        console.log("API Response:", response.data);
        const formattedData = response.data.map(item => ({
          id: item.id,
          wing: item.wingName || "N/A",
          flatNo: item.flatNo || "N/A",
          prevReading: item.previousReading || 0,
          currReading: item.currentReading || 0,
          difference: item.difference || 0,
          // image: item.fileData ? `data:image/jpeg;base64,${item.fileData}` : null,
          date: item.date ? new Date(item.date).toISOString().split('T')[0] : "N/A",
        }));
        console.log("Formatted Data:", formattedData);
        setReportsData(formattedData);
      })
      .catch(error => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  }, []);


  function getImageById(id) {
    setModalImage(null);
    api.get("/meter-readings/getImageById/" + id)
      .then(response => {

        // console.log(response.data.image);
        setModalImage(response.data.image);
      })
      .catch(error => console.error("Error fetching data:", error))
      .finally(() => setLoading(false));
  }
  const formatDate = (date) => {
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    return new Date(date).toLocaleDateString('en-GB', options).replace(',', '');
  };

  const filteredData = reportsData
    .filter((report) => report.wing.includes(filterWing))
    .filter((report) => report.flatNo.includes(filterFlat))
    .filter((report) =>
      (!startDate || new Date(report.date) >= new Date(startDate)) &&
      (!endDate || new Date(report.date) <= new Date(endDate))
    )
    .filter((report) =>
      report.wing.toLowerCase().includes(search.toLowerCase()) ||
      report.flatNo.includes(search)
    );

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const exportFormattedReport = () => {
    // Group readings by flatNo and by month
    const readingsByFlat = {};
    const monthsSet = new Set();

    filteredData.forEach(report => {
      if (!readingsByFlat[report.flatNo]) {
        readingsByFlat[report.flatNo] = {};
      }
      const date = new Date(report.date);
      const monthKey = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      monthsSet.add(monthKey);
      readingsByFlat[report.flatNo][monthKey] = {
        reading: report.currReading,
        date: formatDate(report.date)
      };
    });

    const sortedMonths = Array.from(monthsSet).sort((a, b) => new Date(a) - new Date(b));
    const flatNos = Object.keys(readingsByFlat).sort();

    // Build worksheet data with merged headers
    const wsData = [];

    // Row 1: Merged month headers
    const headerRow1 = ["Flat No."];
    sortedMonths.forEach(month => {
      headerRow1.push(month, "");
    });
    wsData.push(headerRow1);

    // Row 2: Sub-headers
    const headerRow2 = ["Flat No."];
    sortedMonths.forEach(() => {
      headerRow2.push("Meter reading", "Date of reading");
    });
    wsData.push(headerRow2);

    // Data rows
    flatNos.forEach(flatNo => {
      const row = [flatNo];
      sortedMonths.forEach(month => {
        const data = readingsByFlat[flatNo][month];
        row.push(data ? parseFloat(data.reading.toFixed(2)) : "", data ? data.date : "");
      });
      wsData.push(row);
    });

    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Merge cells for month headers (row 0)
    const merges = [];
    sortedMonths.forEach((_, index) => {
      const startCol = 1 + (index * 2);
      const endCol = startCol + 1;
      merges.push({ s: { r: 0, c: startCol }, e: { r: 0, c: endCol } });
    });
    ws['!merges'] = merges;

    // Set column widths
    ws['!cols'] = [{ wch: 12 }, ...sortedMonths.flatMap(() => [{ wch: 16 }, { wch: 18 }])];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, ws, "Formatted Report");
    XLSX.writeFile(workbook, "formatted_report.xlsx");
  };

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredData.map(report => ({
      Building: report.wing,
      "Flat No": report.flatNo,
      // "Previous Reading": report.prevReading.toFixed(2),
      "Current Reading": report.currReading.toFixed(2),
      // Difference: (report.currReading - report.prevReading).toFixed(2),
      Date: formatDate(report.date),
    })));

    worksheet["!protect"] = {
      password: "admin@123",
      selectLockedCells: true,
      selectUnlockedCells: true,
      formatCells: false,
      formatColumns: false,
      formatRows: false,
      insertColumns: false,
      insertRows: false,
      insertHyperlinks: false,
      deleteColumns: false,
      deleteRows: false,
      sort: false,
      autoFilter: false,
      pivotTables: false,
      objects: false,
      scenarios: false,
    };

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reports");

    // Write the file with sheet protection
    XLSX.writeFile(workbook, "reports.xlsx");
  };

  return (
    <div className="admin-layout">
      <Sidebar />
      <div className="admin-workspace">
        <div className="reports-wrapper" style={{ minHeight: "100vh", width: "100%", position: "relative" }}>
          <nav className="reports-navbar" style={{ position: "relative", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 20px", width: "100%" }}>
            <span className="reports-title">Water Billing Reports</span>
            <div style={{ display: 'none' }}><LogoutButton /></div>
          </nav>
      {/* <div className="reports-search-container">
        <input
          type="text"
          placeholder="Search by Wing, Flat No, or Date..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="reports-search"
        />
      </div> */}

      <div className="reports-filter-container">
        <input
          type="text"
          placeholder="Search by Wing, Flat No, or Date..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="reports-search"
        />
        <input type="text" placeholder="Filter by Wing..." value={filterWing} onChange={(e) => setFilterWing(e.target.value)} className="reports-filter" />
        <input type="text" placeholder="Filter by Flat No..." value={filterFlat} onChange={(e) => setFilterFlat(e.target.value)} className="reports-filter" />
      </div>

      <div className="reports-filter-containerr">
        <input
          type="text"
          value={startDate ? new Date(startDate).toLocaleDateString() : ""}
          onFocus={(e) => (e.target.type = "date")}
          onBlur={(e) => (e.target.type = "text")}
          onChange={(e) => setStartDate(e.target.value)}
          placeholder="Select Start Date"
          className="reports-filter"
        />

        <input
          type="text"
          value={endDate ? new Date(endDate).toLocaleDateString() : ""}
          onFocus={(e) => (e.target.type = "date")}
          onBlur={(e) => (e.target.type = "text")}
          onChange={(e) => setEndDate(e.target.value)}
          placeholder="Select End Date"
          className="reports-filter"
        />

        <button className="export-btn" onClick={exportToExcel}>
          <i className="fas fa-file-excel"></i> Export to Excel
        </button>
        <button className="export-btn" onClick={exportFormattedReport}>
          <i className="fas fa-file-alt"></i> Formatted Report
        </button>
      </div>

      <div className="reports-table-wrapper">
        <table className="reports-table">
          <thead>
            <tr>
              <th>Building</th>
              <th>Flat No</th>
              {/* <th>Previous Reading</th> */}
              <th>Current Reading</th>
              {/* <th>Difference</th> */}
              <th>Uploaded Image</th>
              <th>Date</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7" className="table-loader">Loading...</td></tr>
            ) : paginatedData.length === 0 ? (
              <tr><td colSpan="7">No reports available</td></tr>
            ) : (
              paginatedData.map(report => (
                <tr key={report.id}>
                  <td>{report.wing}</td>
                  <td>{report.flatNo}</td>
                  {/* <td>{report.prevReading.toFixed(2)}</td> */}
                  <td>{report.currReading.toFixed(2)}</td>
                  {/* <td>{report.difference.toFixed(2)}</td> */}
                  <td>
                    {<button onClick={() => { getImageById(report.id) }}>View Image</button>}
                  </td>
                  <td>{formatDate(report.date)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {modalImage && (
        <div className="modal" onClick={() => setModalImage(null)}>
          <img src={`${modalImage}`} alt="Meter" className="modal-content" />
        </div>
      )}
      <div className="pagination">
        <button disabled={
          currentPage === 1
        }
          onClick={() => setCurrentPage(currentPage - 1)}>prev</button>

        <span>page{currentPage} of {totalPages}</span>
        <button disabled={
          currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}>Next</button>
      </div>
      <div className="footer">
        <p>powered by @Dexpert Systems</p>
      </div>
    </div>
      </div>
    </div>
  );
}

export default AdminReports;